"use client";

import Image from "next/image";
import { Link } from "@/i18n/navigation";
import { MapPin, Music } from "lucide-react";
import { useLocale } from "next-intl";
import { FestivalSummary } from "@/lib/types";
import { formatFestivalDateRanges, isOwnImage } from "@/lib/utils";

interface FestivalCardProps {
  festival: FestivalSummary;
  citySlug: string;
}

export function FestivalCard({ festival, citySlug }: FestivalCardProps) {
  const locale = useLocale();
  const imageUrl =
    festival.image_url && isOwnImage(festival.image_url)
      ? festival.image_url
      : null;
  const dateRange = formatFestivalDateRanges(festival.day_dates, locale);
  const effectiveCitySlug = festival.city_slug ?? citySlug;
  const href = `/${effectiveCitySlug}/event/${festival.slug}`;

  return (
    <div className="group relative overflow-hidden rounded-lg border border-acid/20 bg-surface transition-all duration-300 hover:border-acid/40 hover:shadow-[0_0_30px_rgba(209,248,5,0.08)]">
      <Link
        href={href}
        rel="nofollow" prefetch={false}
        className="absolute inset-0 z-[1]"
        aria-label={`View ${festival.name}`}
      />

      {/* Image */}
      <div className="relative aspect-video overflow-hidden bg-neutral-900">
        {imageUrl ? (
          <Image
            src={imageUrl}
            alt={festival.name}
            fill
            className="object-cover transition-transform duration-500 group-hover:scale-105"
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
          />
        ) : (
          <div className="flex h-full items-center justify-center bg-gradient-to-br from-acid/5 to-acid/10">
            <Music className="h-8 w-8 text-acid/30" />
          </div>
        )}

        {/* Festival badge */}
        <div className="absolute top-3 left-3 z-10 rounded bg-acid px-2 py-0.5 font-mono text-[10px] font-bold text-black uppercase tracking-wider">
          Festival
        </div>

        {/* Artist count */}
        {festival.total_artists > 0 && (
          <div className="absolute top-3 right-3 z-10 rounded bg-black/80 px-2 py-0.5 font-mono text-[10px] font-bold text-acid backdrop-blur-sm">
            {festival.total_artists} artists
          </div>
        )}
      </div>

      {/* Content */}
      <div className="px-4 pb-4 pt-2">
        <h3 className="mb-1 text-sm font-semibold leading-tight text-white line-clamp-1">
          {festival.name}
        </h3>

        {/* Date range */}
        {dateRange && (
          <p className="mb-2 font-mono text-xs text-acid/80">{dateRange}</p>
        )}

        {/* Venue */}
        {festival.venue_name && (
          <div className="flex items-center gap-1.5">
            <MapPin className="h-3 w-3 shrink-0 text-neutral-500" />
            {festival.venue_slug ? (
              <Link
                href={`/${effectiveCitySlug}/${festival.venue_slug}`}
                onClick={(e) => e.stopPropagation()}
                className="relative z-10 pointer-events-auto text-xs text-neutral-400 line-clamp-1 hover:text-acid transition-colors"
              >
                {festival.venue_name}
              </Link>
            ) : (
              <span className="text-xs text-neutral-400 line-clamp-1">
                {festival.venue_name}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
